import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"
import { useTranslation } from "react-i18next"

import type { ToolConfig, ToolConfigValue, ToolParameters } from "./tool-node-utils"
import type { NodeProps } from "@xyflow/react"

import { HelpCircle, Minus, Plus, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { FlowPluginToolConfig } from "@/features/plugins/flow-plugin-picker"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { LibraryPicker } from "./library-picker"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"
import {
  toolConfigString as asString,
  getFlowLibraryOptions,
  getM365ToolActionAllowList,
  M365_TOOL_ACTIONS,
  parseM365Recipients,
  WEB_SEARCH_MODES,
} from "./tool-node-utils"
import { TrustedSourcesPicker } from "./trusted-sources-picker"

const TOOL_BADGE_CLASS = "text-chart-foreground border-chart-foreground text-[10px]"

export function ToolNode({ id, data, selected }: NodeProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.agentNode" })
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)
  const toolName = data.toolName as string

  const handleConfigChange = useCallback(
    (key: string, value: ToolConfigValue) => {
      const currentConfig =
        data.config && typeof data.config === "object" ? (data.config as ToolConfig) : {}
      updateNodeData(id, { config: { ...currentConfig, [key]: value } })
    },
    [id, data.config, updateNodeData]
  )

  // Trusted sources state (web_search) — stored alongside searchMode in data.config
  const webSearchConfig =
    data.config && typeof data.config === "object" ? (data.config as ToolConfig) : {}
  const selectedTrustedSourceUrls: string[] = Array.isArray(webSearchConfig.selectedTrustedSources)
    ? (webSearchConfig.selectedTrustedSources as string[])
    : []
  const trustedMode: "only" | "preferred" =
    webSearchConfig.trustedMode === "preferred" ? "preferred" : "only"

  const toggleTrustedSource = useCallback(
    (sourceUrl: string) => {
      const isActive = selectedTrustedSourceUrls.includes(sourceUrl)
      const newSelection = isActive
        ? selectedTrustedSourceUrls.filter((url) => url !== sourceUrl)
        : [...selectedTrustedSourceUrls, sourceUrl]
      handleConfigChange("selectedTrustedSources", newSelection)
    },
    [selectedTrustedSourceUrls, handleConfigChange]
  )

  const handleTrustedModeChange = useCallback(
    (mode: "only" | "preferred") => {
      handleConfigChange("trustedMode", mode)
    },
    [handleConfigChange]
  )

  // Library selection state (library_search) — stored in data.config, mirrors the
  // Agent node's per-node library override: same LibraryPicker component, same
  // "empty selection = flow-wide default" semantics enforced server-side.
  const storedLibraries: string[] = Array.isArray(webSearchConfig.selectedLibraries)
    ? (webSearchConfig.selectedLibraries as string[])
    : []
  const availableLibraries = getFlowLibraryOptions(SettingsService.getInstance().settings.indexes)
  // Default to all libraries selected when none are explicitly chosen (display only —
  // nothing is persisted until the user actually toggles a library).
  const selectedLibraries =
    storedLibraries.length > 0 ? storedLibraries : availableLibraries.map((lib) => lib.id)

  const toggleLibrary = useCallback(
    (libraryId: string) => {
      const isActive = selectedLibraries.includes(libraryId)
      const newSelection = isActive
        ? selectedLibraries.filter((lid) => lid !== libraryId)
        : [...selectedLibraries, libraryId]
      handleConfigChange("selectedLibraries", newSelection)
    },
    [selectedLibraries, handleConfigChange]
  )

  const handleM365ActionChange = useCallback(
    (value: string) => {
      const currentConfig =
        data.config && typeof data.config === "object" ? (data.config as ToolConfig) : {}
      const m365Actions = getM365ToolActionAllowList(value)
      updateNodeData(id, {
        config: { ...currentConfig, action: value },
        ...(m365Actions ? { m365Actions } : {}),
      })
    },
    [id, data.config, updateNodeData]
  )

  const handleM365ParameterChange = useCallback(
    (key: string, value: string | string[]) => {
      const currentConfig =
        data.config && typeof data.config === "object" ? (data.config as ToolConfig) : {}
      const currentParameters =
        currentConfig.parameters && typeof currentConfig.parameters === "object"
          ? (currentConfig.parameters as ToolParameters)
          : {}
      updateNodeData(id, {
        config: {
          ...currentConfig,
          parameters: { ...currentParameters, [key]: value },
        },
      })
    },
    [id, data.config, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  // Determine inputs based on tool name
  const renderConfigInputs = (isModal = false) => {
    const config = data.config && typeof data.config === "object" ? (data.config as ToolConfig) : {}
    if (toolName === "plugin_execute")
      return (
        <FlowPluginToolConfig
          config={config}
          onChange={(next, bindings) =>
            updateNodeData(id, { config: next, plugin_bindings: bindings })
          }
        />
      )

    if (toolName === "microsoft_365_search") {
      const action = typeof config.action === "string" ? config.action : "get_emails"
      const parameters =
        config.parameters && typeof config.parameters === "object"
          ? (config.parameters as ToolParameters)
          : {}
      let toValue = ""
      if (Array.isArray(parameters.to)) {
        toValue = parameters.to.join(", ")
      } else if (typeof parameters.to === "string") {
        toValue = parameters.to
      }

      return (
        <div className="mt-2 grid gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs">Action</Label>
            <Select value={action} onValueChange={handleM365ActionChange}>
              <SelectTrigger className="nodrag h-7 text-xs">
                <SelectValue placeholder="Select action..." />
              </SelectTrigger>
              <SelectContent>
                {M365_TOOL_ACTIONS.map((action) => (
                  <SelectItem key={action.value} value={action.value}>
                    {action.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {action === "send_email" ? (
            <>
              <div className="grid gap-1.5">
                <Label className="text-xs">To</Label>
                <Input
                  className="nodrag h-7 text-xs"
                  placeholder="daniel@x-one.app"
                  value={toValue}
                  onChange={(e) =>
                    handleM365ParameterChange("to", parseM365Recipients(e.target.value))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Subject</Label>
                <TemplateField
                  className="h-7 pr-8"
                  placeholder="Email subject..."
                  value={typeof parameters.subject === "string" ? parameters.subject : ""}
                  onChange={(value) => handleM365ParameterChange("subject", value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Body</Label>
                <TemplateField
                  multiline
                  className={cn("pr-8", isModal ? "min-h-[320px]" : "min-h-16")}
                  placeholder="Email body..."
                  value={typeof parameters.body === "string" ? parameters.body : ""}
                  onChange={(value) => handleM365ParameterChange("body", value)}
                />
              </div>
            </>
          ) : (
            <div className="grid gap-1.5">
              <Label className="text-xs">Search Query (Optional)</Label>
              <Input
                className="nodrag h-7 text-xs"
                placeholder="Search query..."
                value={asString(config.query)}
                onChange={(e) => handleConfigChange("query", e.target.value)}
              />
            </div>
          )}
        </div>
      )
    }

    if (toolName === "web_search") {
      return (
        <div className="mt-2 grid gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs">Static Query (Optional)</Label>
            <Input
              className="nodrag h-7 text-xs"
              placeholder="Fixed search query..."
              value={asString(config.query)}
              onChange={(e) => handleConfigChange("query", e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <div className="flex items-center gap-1">
              <Label className="text-xs">Search Mode</Label>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="text-muted-foreground size-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[250px]" side="top">
                    <p className="text-xs">
                      <strong>All web sources:</strong> Search the entire web normally.
                    </p>
                    <p className="mt-1 text-xs">
                      <strong>Only trusted sources:</strong> Restrict search results to domains
                      you&apos;ve added as trusted sources in your settings. Results will only come
                      from your personal and company trusted domains.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={asString(config.searchMode) || "all"}
              onValueChange={(value) => handleConfigChange("searchMode", value)}
            >
              <SelectTrigger className="nodrag h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEB_SEARCH_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs">Trusted sources</Label>
            <TrustedSourcesPicker
              selectedUrls={selectedTrustedSourceUrls}
              trustedMode={trustedMode}
              onModeChange={handleTrustedModeChange}
              onToggleSource={toggleTrustedSource}
            />
          </div>
        </div>
      )
    }

    if (toolName === "library_search") {
      return (
        <div className="mt-2 grid gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs">Static Query (Optional)</Label>
            <Input
              className="nodrag h-7 text-xs"
              placeholder="Fixed search query..."
              value={asString(config.query)}
              onChange={(e) => handleConfigChange("query", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs">{t("libraryPicker.selectLibraries")}</Label>
            <LibraryPicker
              availableLibraries={availableLibraries}
              isUsingDefault={storedLibraries.length === 0}
              selectedLibraries={selectedLibraries}
              onToggleLibrary={toggleLibrary}
            />
          </div>
        </div>
      )
    }

    if (["web_scrape"].includes(toolName)) {
      // Support multiple URLs - migrate from single url to urls array
      const getUrls = (): string[] => {
        if (Array.isArray(config.urls)) {
          return config.urls as string[]
        }
        if (config.url) {
          return [config.url as string]
        }
        return [""]
      }
      const urls = getUrls()

      const handleUrlChange = (index: number, value: string) => {
        const newUrls = [...urls]
        newUrls[index] = value
        handleConfigChange("urls", newUrls)
      }

      const addUrl = () => {
        handleConfigChange("urls", [...urls, ""])
      }

      const removeUrl = (index: number) => {
        if (urls.length > 1) {
          const newUrls = urls.filter((_, i) => i !== index)
          handleConfigChange("urls", newUrls)
        }
      }

      return (
        <div className="mt-2 grid gap-2">
          <Label className="text-xs">Target URLs (Optional)</Label>
          {urls.map((url, index) => (
            <div key={index} className="flex items-center gap-1">
              <Input
                className="nodrag h-7 flex-1 text-xs"
                placeholder="https://..."
                value={url}
                onChange={(e) => handleUrlChange(index, e.target.value)}
              />
              {urls.length > 1 && (
                <Button
                  className="nodrag size-7 shrink-0 p-0"
                  size="icon"
                  variant="ghost"
                  onClick={() => removeUrl(index)}
                >
                  <Minus className="size-3" />
                </Button>
              )}
            </div>
          ))}
          <Button
            className="nodrag h-7 w-full text-xs"
            size="sm"
            variant="outline"
            onClick={addUrl}
          >
            <Plus className="mr-1 size-3" />
            Add URL
          </Button>
        </div>
      )
    }

    return null
  }

  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div>{renderConfigInputs(isModal)}</div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[220px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[0])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-between text-sm font-medium">
            <div className="flex flex-col">
              <span>{(data.label as string) || "Tool"}</span>
              <span className="text-chart-foreground/80 text-[10px] font-normal">{toolName}</span>
            </div>
            <Badge className={TOOL_BADGE_CLASS} variant="outline">
              Tool
            </Badge>
            <NodeEditDialog
              description="Edit this tool node in a larger workspace."
              title={`${(data.label as string) || "Tool"} settings`}
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          <div className="grid gap-3">
            <div className="text-muted-foreground text-xs">{data.description as string}</div>
            {renderEditor()}
          </div>
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
